import { Controller, type Context } from '@hotwired/stimulus';
import { toggleHidden } from 'src/utils/dom-toggle';

function createWavePath(width: number) {
  let d = 'M0 18 Q12 12 24 18';
  for (let x = 48; x <= width + 120; x += 24) {
    d += ` T${x} 18`;
  }
  return d;
}

export default class TasksLiveController extends Controller<HTMLElement> {
  public static targets = ['bar', 'wave', 'fill'];

  declare private barTarget: SVGSVGElement;
  declare private waveTarget: SVGPathElement;
  declare private fillTarget: SVGRectElement;

  private rafId!: null | ReturnType<typeof requestAnimationFrame>;
  private cancelTimer: ReturnType<typeof setTimeout> | undefined;
  private pendingRequests: number = 0;
  private totalRequests: number = 0;
  private resizeObserver!: ResizeObserver;

  constructor(context: Context) {
    super(context);

    this.scheduleSync = this.scheduleSync.bind(this);
    this.syncWidth = this.syncWidth.bind(this);

    this.resizeObserver = new ResizeObserver(this.scheduleSync);
  }

  connect(): void {
    this.resizeObserver.observe(this.barTarget);
    this.scheduleSync();
  }

  public onHasPendingRequest(_event: Event) {
    clearTimeout(this.cancelTimer);

    if (this.pendingRequests === 0) {
      this.setMode('indeterminate');
    }
  }

  public onStartRequest(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const customEvent = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        count: number;
      };
    };

    if (!('count' in customEvent.detail)) {
      throw new Error(
        'Expected count in detail, actual: ' +
          Object.keys(customEvent.detail).toString(),
      );
    }

    const { count } = customEvent.detail;

    clearTimeout(this.cancelTimer);
    this.pendingRequests += count;
    this.totalRequests += count;

    this.setMode('determinate');

    const done = this.totalRequests - this.pendingRequests;
    this.setProgress(Math.max(0.1, done / this.totalRequests) * 100);
  }

  public onFinishRequest(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const customEvent = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        count: number;
        success: boolean;
        error?: Error;
      };
    };

    if (!('count' in customEvent.detail)) {
      throw new Error(
        'Expected count in detail, actual: ' +
          Object.keys(customEvent.detail).toString(),
      );
    }

    const { count } = customEvent.detail;

    clearTimeout(this.cancelTimer);
    this.pendingRequests -= count;

    const done = this.totalRequests - this.pendingRequests;
    this.setProgress(Math.max(0.1, done / this.totalRequests) * 100);

    if (this.pendingRequests === 0) {
      this.element.dataset.busy = 'false';
      this.cancelTimer = setTimeout(() => {
        this.totalRequests = 0;
        this.setActive(false);
      }, 750);
    }
  }

  private scheduleSync() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(this.syncWidth);
  }

  private syncWidth() {
    const w = Math.round(this.barTarget.getBoundingClientRect().width);
    this.barTarget.setAttribute('viewBox', `0 0 ${w} 32`);
    this.waveTarget.setAttribute('d', createWavePath(w));
    this.element.style.setProperty('--vb-w', `${w}px`);

    this.rafId = null;
  }

  public setActive(next: boolean) {
    this.element.dataset.busy = next ? 'true' : 'false';
    toggleHidden(this.element, !next);
  }

  public setMode(mode: 'indeterminate' | 'determinate') {
    this.setActive(true);

    this.element.style.setProperty('--mode', mode);
    this.element.dataset.mode = mode;

    if (mode === 'indeterminate') {
      this.fillTarget.setAttribute('aria-valuenow', '0');
    }
  }

  public setProgress(value: number) {
    this.setActive(true);

    const pct = Math.min(100, Math.max(0, value));
    this.element.style.setProperty('--progress', pct + '%');
    this.fillTarget.setAttribute('aria-valuenow', String(Math.round(pct)));

    if (this.element.dataset.mode !== 'determinate') {
      this.setMode('determinate');
    }
  }

  disconnect(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.resizeObserver.unobserve(this.barTarget);
  }
}
